<template>
  <v-form ref="form" v-model="valid">
    <h3 class="title mb-4">Property Information</h3>
    
    <!-- Property existence check -->
    <v-radio-group
      v-model="propertyData.isExisting"
      row
      class="mb-4"
    >
      <template v-slot:label>
        <div class="text-subtitle-1 black--text">
          Does this property already exist in agentPay?
        </div>
      </template>
      <v-radio label="Yes" :value="true"></v-radio>
      <v-radio label="No" :value="false"></v-radio>
    </v-radio-group>
    
    <!-- Existing property search -->
    <div v-if="propertyData.isExisting === true" class="mb-4">
      <autocomplete-search
        v-model="propertyData.existingProperty"
        :search-function="searchFunction"
        label="Search for existing property"
        placeholder="Enter at least 5 characters to search..."
        :rules="[rules.required]"
        @change="handlePropertySelection"
      />
      
      <v-card v-if="propertyData.existingProperty" class="mt-4" outlined>
        <v-card-text>
          <div class="text-subtitle-2 mb-2">Selected Property:</div>
          <div>{{ propertyData.existingProperty.Subject }}</div>
          <div class="text-caption">ID: {{ propertyData.existingProperty.OID }}</div>
        </v-card-text>
      </v-card>
      
      <!-- Property verification status -->
      <v-alert
        v-if="propertyData.existingProperty && propertyVerification.loading"
        type="info"
        outlined
        class="mt-4"
      >
        <v-progress-circular
          indeterminate
          color="info"
          size="20"
          width="2"
          class="mr-2"
        ></v-progress-circular>
        Verifying property eligibility...
      </v-alert>
      
      <!-- Show advisories if any -->
      <v-alert
        v-if="propertyData.existingProperty && !propertyVerification.loading && propertyVerification.advisories.length > 0"
        :type="propertyVerification.canSubmit ? 'warning' : 'error'"
        outlined
        class="mt-4"
      >
        <div class="font-weight-medium mb-2">
          {{ propertyVerification.canSubmit ? 'Property Advisory:' : 'Property Cannot Be Used:' }}
        </div>
        <ul class="mb-0 pl-4">
          <li v-for="(advisory, index) in propertyVerification.advisories" :key="index">
            {{ advisory }}
          </li>
        </ul>
      </v-alert>
      
      <!-- Success message if property is verified and no advisories -->
      <v-alert
        v-if="propertyData.existingProperty && !propertyVerification.loading && propertyVerification.canSubmit && propertyVerification.advisories.length === 0"
        type="success"
        outlined
        class="mt-4"
      >
        <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
        Property verified and ready for tenancy creation.
      </v-alert>
    </div>
    
    <!-- New property form -->
    <div v-else-if="propertyData.isExisting === false">
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="propertyData.newProperty.addressNumber"
            label="Address Number"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="9">
          <v-text-field
            v-model="propertyData.newProperty.address1"
            label="Address Line 1"
            outlined
            dense
            :rules="[rules.required]"
            required
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="propertyData.newProperty.address2"
            label="Address Line 2"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="propertyData.newProperty.address3"
            label="Address Line 3"
            outlined
            dense
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="propertyData.newProperty.address4"
            label="Address Line 4"
            outlined
            dense
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="propertyData.newProperty.postcode"
            label="Postcode"
            outlined
            dense
            :rules="[rules.required, rules.postcode]"
            required
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="propertyData.newProperty.type"
            :items="propertyTypes"
            label="Property Type"
            outlined
            dense
            item-text="text"
            item-value="value"
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="propertyData.newProperty.description"
            label="Description"
            outlined
            dense
            rows="3"
            placeholder="Optional property description..."
          />
        </v-col>
      </v-row>
      
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="propertyData.newProperty.facilities"
            label="Facilities"
            outlined
            dense
            rows="3"
            placeholder="Optional facilities information..."
          />
        </v-col>
      </v-row>
    </div>
    
    <!-- Navigation buttons -->
    <v-row class="mt-6">
      <v-col cols="12" class="text-right">
        <v-btn
          color="primary"
          :disabled="!canProceed || propertyVerification.loading"
          :loading="propertyVerification.loading"
          @click="proceed"
        >
          Next
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AutocompleteSearch from '@/components/Tenancies/AutocompleteSearch.vue'

export default {
  name: 'PropertyStep',
  
  components: {
    AutocompleteSearch
  },
  
  props: {
    value: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    searchFunction: {
      type: Function,
      required: true
    }
  },
  
  data() {
    return {
      valid: false,
      propertyData: this.value,
      propertyTypes: [
        { value: 0, text: 'House' },
        { value: 1, text: 'Flat' },
        { value: 2, text: 'Bungalow' },
        { value: 3, text: 'Studio' },
        { value: 4, text: 'Other' }
      ],
      rules: {
        required: v => !!v || 'This field is required',
        postcode: v => {
          if (!v) return true
          const postcodeRegex = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i
          return postcodeRegex.test(v) || 'Please enter a valid UK postcode'
        }
      }
    }
  },
  
  computed: {
    ...mapGetters('TenanciesManagement', ['propertyVerification', 'canSubmitTenancy']),
    
    canProceed() {
      if (this.propertyData.isExisting === null) {
        return false
      }
      
      if (this.propertyData.isExisting) {
        // For existing properties, check both selection and verification
        return !!this.propertyData.existingProperty && this.canSubmitTenancy
      } else {
        // For new properties, just check form validation
        return this.valid && 
               this.propertyData.newProperty.address1 && 
               this.propertyData.newProperty.postcode
      }
    }
  },
  
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.propertyData = newVal
      }
    },
    propertyData: {
      deep: true,
      handler(val) {
        this.$emit('input', val)
      }
    },
    'propertyData.isExisting': function(newVal) {
      // Clear verification when switching between existing/new property
      if (newVal !== null) {
        this.clearPropertyVerification()
      }
    }
  },
  
  methods: {
    ...mapActions('TenanciesManagement', ['verifyProperty', 'clearPropertyVerification']),
    
    async handlePropertySelection() {
      // Auto-populate any additional fields if needed
      
      // Verify the selected property
      if (this.propertyData.existingProperty && this.propertyData.existingProperty.OID) {
        await this.verifyProperty(this.propertyData.existingProperty.OID)
      }
    },
    
    proceed() {
      if (this.canProceed) {
        this.$emit('next')
      }
    }
  },
  
  mounted() {
    // Clear any previous verification when component mounts
    this.clearPropertyVerification()
  }
}
</script>